// src/components/layout/Header.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CartContext } from '../../context/CartContext';
import axios from 'axios';
import { getCategoriesWithImages } from '../../services/api';
import { LANGUAGES, changeLanguage } from '../../i18n';

function Header({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [scrolled, setScrolled]                     = useState(false);
  const [menuOpen, setMenuOpen]                     = useState(false);
  const [showUserMenu, setShowUserMenu]             = useState(false);
  const [showNotifs, setShowNotifs]                 = useState(false);
  const [showSearch, setShowSearch]                 = useState(false);
  const [showLangMenu, setShowLangMenu]             = useState(false);
  const [searchQuery, setSearchQuery]               = useState('');
  const [searchResults, setSearchResults]           = useState([]);
  const [searching, setSearching]                   = useState(false);
  const [notifications, setNotifications]           = useState([]);
  const [unreadCount, setUnreadCount]               = useState(0);
  const [isAuthenticated, setIsAuthenticated]       = useState(false);
  const [userData, setUserData]                     = useState(null);

  const [categories, setCategories]                 = useState([]);
  const [showBoutiqueMenu, setShowBoutiqueMenu]     = useState(false);
  const [mobileBoutiqueOpen, setMobileBoutiqueOpen] = useState(false);

  const searchInputRef = useRef(null);
  const { cartItemCount = 0 } = useContext(CartContext) || {};

  /* ========================= AUTH ========================= */
  useEffect(() => {
    const token  = localStorage.getItem('auth_token');
    const userId = localStorage.getItem('user_id');
    const stored = localStorage.getItem('user_data');

    if (token && userId) {
      setIsAuthenticated(true);
      if (user) setUserData(user);
      else if (stored) {
        try { setUserData(JSON.parse(stored)); } catch { setUserData(null); }
      }
    } else {
      setIsAuthenticated(false);
      setUserData(null);
    }
  }, [user]);

  /* ========================= SCROLL ========================= */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ========================= RESIZE ========================= */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ========================= SCROLL LOCK MOBILE MENU ========================= */
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  /* ========================= CLIC EXTÉRIEUR ========================= */
  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest('.header__user-wrap'))     setShowUserMenu(false);
      if (!e.target.closest('.header__notif-wrap'))    setShowNotifs(false);
      if (!e.target.closest('.header__search-wrap'))   setShowSearch(false);
      if (!e.target.closest('.header__nav-item-wrap')) setShowBoutiqueMenu(false);
      if (!e.target.closest('.header__lang-wrap'))     setShowLangMenu(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  /* ========================= FOCUS RECHERCHE ========================= */
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showSearch]);

  /* ========================= CATÉGORIES ========================= */
  useEffect(() => {
    getCategoriesWithImages()
      .then(cats => {
        const list = Array.isArray(cats) ? cats : [];
        const formatted = list
          .filter(cat => cat && (cat.name || cat.categorie))
          .map((cat, i) => {
            const name = cat.name || cat.categorie || '';
            const slug = cat.slug || name.toLowerCase()
              .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              .replace(/\s+/g, '-');
            return { label: name, slug: slug || `cat-${i}` };
          });
        setCategories(formatted);
      })
      .catch(() => setCategories([]));
  }, []);

  /* ========================= RECHERCHE ========================= */
  useEffect(() => {
    if (searchQuery.trim().length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(
          'https://madadline.com/api/products/search',
          { params: { query: searchQuery.trim() } }
        );
        setSearchResults(Array.isArray(data) ? data.slice(0, 5) : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?query=${encodeURIComponent(searchQuery.trim())}`);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    setMenuOpen(false);
  };

  const goToProduct = (id) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/product/${id}`);
  };

  /* ========================= NOTIFICATIONS ========================= */
  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    const fetchNotifs = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const userId = localStorage.getItem('user_id');
        const { data } = await axios.get(
          `https://madadline.com/api/notifications/user/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNotifications(Array.isArray(data) ? data : []);
        setUnreadCount((data || []).filter(n => !n.read).length);
      } catch {
        setNotifications([]);
        setUnreadCount(0);
      }
    };
    fetchNotifs();
    const id = setInterval(fetchNotifs, 30000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  /* ========================= LANGUE ========================= */
  const handleLangChange = (code) => {
    changeLanguage(code);
    setShowLangMenu(false);
    setMenuOpen(false);
  };

  /* ========================= LOGOUT ========================= */
  const handleLogout = () => {
    if (typeof onLogout === 'function') onLogout();
    localStorage.removeItem('cart');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_data');
    setIsAuthenticated(false);
    setUserData(null);
    setShowUserMenu(false);
    setMenuOpen(false);
    navigate('/');
  };

  /* ========================= INITIALES ========================= */
  const getInitials = () => {
    const name = userData?.name || userData?.email || 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name[0].toUpperCase();
  };

  /* ========================= DATE NOTIF ========================= */
  const formatNotifTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = (new Date() - new Date(dateStr)) / 1000;
    if (diff < 60)    return t('notifications.justNow');
    if (diff < 3600)  return t('notifications.minutesAgo', { n: Math.floor(diff / 60) });
    if (diff < 86400) return t('notifications.hoursAgo',   { n: Math.floor(diff / 3600) });
    return              t('notifications.daysAgo',   { n: Math.floor(diff / 86400) });
  };

  const currentShort = LANGUAGES.find(l => l.code === i18n.language)?.short || 'FR';

  return (
    <>
      <header className={`header ${scrolled ? 'header--scrolled' : ''}`}>
        <div className="header__inner">

          {/* BURGER mobile */}
          <button
            className="header__icon header__burger"
            onClick={() => setMenuOpen(true)}
            aria-label="Menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          {/* LOGO */}
          <Link to="/" className="header__logo" aria-label="MADAD — Accueil">
            <svg className="header__logo-svg" viewBox="0 0 180 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <line x1="10" y1="32" x2="34" y2="32" stroke="#C21E56" strokeWidth="0.8" opacity="0.5" />
              <line x1="146" y1="32" x2="170" y2="32" stroke="#C21E56" strokeWidth="0.8" opacity="0.5" />
              <text x="90" y="24" textAnchor="middle" fontFamily="'Tajawal', 'Arial', sans-serif" fontSize="20" fontWeight="700" fill="#C21E56">مداد</text>
              <text x="90" y="48" textAnchor="middle" fontFamily="'Cormorant Garamond', Georgia, serif" fontSize="22" fontWeight="700" fill="#C21E56" letterSpacing="7">MADAD</text>
              <text x="90" y="60" textAnchor="middle" fontFamily="'Inter', sans-serif" fontSize="5.5" fontWeight="500" fill="#252525" opacity="0.55" letterSpacing="2.5">WHERE STYLE MEETS SOUL</text>
            </svg>
          </Link>

          {/* NAV DESKTOP */}
          <nav className="header__nav">
            <NavLink to="/" end>{t('nav.home')}</NavLink>

            <div className="header__nav-item-wrap">
              <button
                type="button"
                className={`header__nav-trigger ${showBoutiqueMenu ? 'is-open' : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowBoutiqueMenu(v => !v); }}
                aria-haspopup="true"
                aria-expanded={showBoutiqueMenu}
              >
                <span>{t('nav.shop')}</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                     className="header__nav-chevron">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showBoutiqueMenu && (
                <div className="header__nav-dropdown" onClick={e => e.stopPropagation()}>
                  <div className="header__nav-dropdown-head">
                    <span className="header__nav-dropdown-eyebrow">{t('nav.collections')}</span>
                    <h4 className="header__nav-dropdown-title">{t('nav.exploreShop')}</h4>
                  </div>

                  <Link to="/boutique" className="header__nav-dropdown-all" onClick={() => setShowBoutiqueMenu(false)}>
                    <span className="header__nav-dropdown-all-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <rect x="3" y="3" width="7" height="7" rx="1.5" />
                        <rect x="14" y="3" width="7" height="7" rx="1.5" />
                        <rect x="14" y="14" width="7" height="7" rx="1.5" />
                        <rect x="3" y="14" width="7" height="7" rx="1.5" />
                      </svg>
                    </span>
                    <span>{t('nav.allShop')}</span>
                    <svg className="header__nav-dropdown-all-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </Link>

                  {categories.length > 0 && <div className="header__nav-dropdown-divider" />}

                  {categories.length > 0 ? (
                    <div className="header__nav-dropdown-grid">
                      {categories.map(cat => (
                        <Link
                          key={cat.slug}
                          to={`/${cat.slug}`}
                          className="header__nav-dropdown-cat"
                          onClick={() => setShowBoutiqueMenu(false)}
                        >
                          <span className="header__nav-dropdown-cat-label">{cat.label}</span>
                          <svg className="header__nav-dropdown-cat-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="header__nav-dropdown-loading">{t('nav.loading')}</div>
                  )}

                  <div className="header__nav-dropdown-foot">
                    <span className="header__nav-dropdown-foot-text">{t('nav.newWeekly')}</span>
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/about">{t('nav.about')}</NavLink>
            <NavLink to="/contact">{t('nav.contact')}</NavLink>
          </nav>

          {/* ACTIONS */}
          <div className="header__actions">

            {/* RECHERCHE */}
            <div className="header__search-wrap">
              <button
                className="header__icon"
                onClick={(e) => { e.stopPropagation(); setShowSearch(!showSearch); }}
                aria-label={t('search.placeholder')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>

              {showSearch && (
                <div className="header__search-dropdown" onClick={e => e.stopPropagation()}>
                  <form className="header__search-form" onSubmit={handleSearchSubmit}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <circle cx="11" cy="11" r="7" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      ref={searchInputRef}
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('search.placeholder')}
                      autoComplete="off"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="header__search-clear"
                        onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                      >✕</button>
                    )}
                  </form>

                  {searchQuery.trim().length >= 2 && (
                    <div className="header__search-results">
                      {searching ? (
                        <div className="header__search-loading"><div className="spinner" /></div>
                      ) : searchResults.length > 0 ? (
                        <>
                          {searchResults.map(p => {
                            const img = p.image?.startsWith('http')
                              ? p.image
                              : `https://madadline.com/${p.image}`;
                            return (
                              <button key={p.id} className="header__search-item" onClick={() => goToProduct(p.id)}>
                                <img src={img} alt={p.name} />
                                <div>
                                  <strong>{p.name}</strong>
                                  <span>{p.price} DH</span>
                                </div>
                              </button>
                            );
                          })}
                          <button className="header__search-all" onClick={handleSearchSubmit}>
                            {t('search.allResults', { q: searchQuery })}
                          </button>
                        </>
                      ) : (
                        <div className="header__search-empty">
                          <p>{t('search.noResult', { q: searchQuery })}</p>
                          <a
                            href={`https://wa.me/212669428878?text=${encodeURIComponent('Bonjour, je cherche : ' + searchQuery)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="header__search-wa"
                          >
                            {t('search.whatsapp')}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* LANGUE */}
            <div className="header__lang-wrap header__hide-mobile">
              <button
                className="header__lang"
                onClick={(e) => { e.stopPropagation(); setShowLangMenu(v => !v); }}
                aria-label={t('lang.label')}
                aria-haspopup="true"
                aria-expanded={showLangMenu}
              >
                {currentShort}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showLangMenu && (
                <div className="header__lang-dropdown" onClick={e => e.stopPropagation()}>
                  {LANGUAGES.map(l => (
                    <button
                      key={l.code}
                      className={`header__lang-option ${i18n.language === l.code ? 'is-active' : ''}`}
                      onClick={() => handleLangChange(l.code)}
                    >
                      <span className="header__lang-option-code">{l.short}</span>
                      <span className="header__lang-option-label">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* NOTIFICATIONS */}
            <div className="header__notif-wrap header__hide-mobile">
              <button
                className="header__icon header__notif-btn"
                onClick={(e) => { e.stopPropagation(); setShowNotifs(!showNotifs); }}
                aria-label={t('notifications.title')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                  <span className="header__cart-count">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>

              {showNotifs && (
                <div className="header__dropdown header__dropdown--notifs" onClick={e => e.stopPropagation()}>
                  <div className="header__notif-head">
                    <strong>{t('notifications.title')}</strong>
                    {unreadCount > 0 && (
                      <button
                        className="header__notif-markall"
                        onClick={() => {
                          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                          setUnreadCount(0);
                        }}
                      >{t('notifications.markAll')}</button>
                    )}
                  </div>

                  {!isAuthenticated ? (
                    <div className="header__notif-empty">
                      <div className="header__notif-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                      </div>
                      <p>{t('notifications.loginRequired')}</p>
                      <Link to="/login" className="header__notif-login-btn">{t('notifications.login')}</Link>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="header__notif-empty">
                      <div className="header__notif-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                      </div>
                      <p>{t('notifications.empty')}</p>
                      <small>{t('notifications.emptyHint')}</small>
                    </div>
                  ) : (
                    <div className="header__notif-list">
                      {notifications.slice(0, 8).map(n => (
                        <div
                          key={n.id}
                          className={`header__notif-item ${!n.read ? 'is-unread' : ''}`}
                          onClick={() => {
                            setNotifications(prev =>
                              prev.map(x => x.id === n.id ? { ...x, read: true } : x)
                            );
                            if (!n.read) setUnreadCount(c => Math.max(0, c - 1));
                          }}
                        >
                          <div className="header__notif-icon">
                            {n.type === 'order' ? '📦' :
                             n.type === 'promo' ? '🎉' :
                             n.type === 'reply' ? '✉️' :
                             n.type === 'message' ? '💬' :
                             n.type === 'welcome' ? '👋' : '🔔'}
                          </div>
                          <div className="header__notif-content">
                            <div className="header__notif-title">
                              {n.title || n.message || t('notifications.title')}
                            </div>
                            {n.body && <div className="header__notif-body">{n.body}</div>}
                            <div className="header__notif-time">
                              {formatNotifTime(n.created_at || n.time)}
                            </div>
                          </div>
                          {!n.read && <span className="header__notif-dot" />}
                        </div>
                      ))}
                    </div>
                  )}

                  {isAuthenticated && notifications.length > 0 && (
                    <Link to="/notifications" className="header__notif-footer" onClick={() => setShowNotifs(false)}>
                      {t('notifications.viewAll')}
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* PANIER */}
            <Link
  to="/cart"
  className="header__icon header__cart header__hide-mobile"
  aria-label={t('account.orders')}
>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {cartItemCount > 0 && (
                <span className="header__cart-count">{cartItemCount}</span>
              )}
            </Link>

            {/* COMPTE */}
            {isAuthenticated ? (
              <div className="header__user-wrap">
                <button
                  className="header__user-btn"
                  onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}
                  aria-label={t('account.profile')}
                >
                  <span className="header__user-avatar">
                    {userData?.avatar ? (
                      <img src={userData.avatar.startsWith('http') ? userData.avatar : `https://madadline.com/${userData.avatar}`} alt="" />
                    ) : (
                      <span className="header__user-initials">{getInitials()}</span>
                    )}
                    <span className="header__user-status" />
                  </span>
                  <span className="header__user-name">
                    {userData?.name?.split(' ')[0] || t('account.account')}
                  </span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="header__user-arrow">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {showUserMenu && (
                  <div className="header__dropdown" onClick={e => e.stopPropagation()}>
                    <div className="header__dropdown-user">
                      <span className="header__dropdown-avatar">
                        {userData?.avatar ? (
                          <img src={userData.avatar.startsWith('http') ? userData.avatar : `https://madadline.com/${userData.avatar}`} alt="" />
                        ) : (
                          <span>{getInitials()}</span>
                        )}
                      </span>
                      <div className="header__dropdown-info">
                        <strong>{userData?.name || t('account.user')}</strong>
                        <small>{userData?.email || ''}</small>
                        <span className="header__dropdown-badge">{t('account.connected')}</span>
                      </div>
                    </div>

                    <div className="header__dropdown-divider" />

                    <Link to="/profile" className="header__dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                      </svg>
                      {t('account.profile')}
                    </Link>
                    <Link to="/cart" className="header__dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                      </svg>
                      {t('account.orders')}
                    </Link>
                    <Link to="/notifications" className="header__dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                      {t('account.notifications')}
                    </Link>

                    <div className="header__dropdown-divider" />

                    <button onClick={handleLogout} className="header__dropdown-item header__dropdown-item--danger">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      {t('account.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="header__login-btn" aria-label={t('account.login')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                </svg>
                <span className="header__login-text">{t('account.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ============ MENU MOBILE ============ */}
      <div
        className={`mm-overlay ${menuOpen ? 'is-open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <aside className={`mm ${menuOpen ? 'is-open' : ''}`}>

        <div className="mm__head">
          <button
            className="mm__close"
            onClick={() => setMenuOpen(false)}
            aria-label="Fermer"
            type="button"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
          <div className="mm__lang">
            {LANGUAGES.map((l, i) => (
              <React.Fragment key={l.code}>
                {i > 0 && <span>|</span>}
                <button
                  type="button"
                  className={i18n.language === l.code ? 'active' : ''}
                  onClick={() => handleLangChange(l.code)}
                >{l.short}</button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {isAuthenticated && (
          <div className="mm__user">
            <span className="mm__user-avatar">
              {userData?.avatar ? (
                <img src={userData.avatar.startsWith('http') ? userData.avatar : `https://madadline.com/${userData.avatar}`} alt="" />
              ) : (
                <span>{getInitials()}</span>
              )}
            </span>
            <div className="mm__user-info">
              <strong>{userData?.name || t('account.user')}</strong>
              <small>{t('account.connected')}</small>
            </div>
          </div>
        )}

        <nav className="mm__links mm__links--main">
          <Link
            to="/"
            className="mm__link mm__link--big"
            onClick={() => setMenuOpen(false)}
          >
            {t('nav.home')}
          </Link>

          <div className="mm__accordion">
            <button
              type="button"
              className="mm__link mm__link--big mm__accordion-trigger"
              onClick={() => setMobileBoutiqueOpen(v => !v)}
            >
              {t('nav.shop')}
              <span className="mm__accordion-chevron">{mobileBoutiqueOpen ? '−' : '+'}</span>
            </button>

            {mobileBoutiqueOpen && (
              <div className="mm__accordion-content">
                <Link
                  to="/boutique"
                  className="mm__accordion-link"
                  onClick={() => setMenuOpen(false)}
                >
                  {t('mobile.viewAllShop')}
                </Link>
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/${cat.slug}`}
                    className="mm__accordion-link"
                    onClick={() => setMenuOpen(false)}
                  >
                    {cat.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/about"
            className="mm__link mm__link--big"
            onClick={() => setMenuOpen(false)}
          >
            {t('nav.about')}
          </Link>
          <Link
            to="/contact"
            className="mm__link mm__link--big"
            onClick={() => setMenuOpen(false)}
          >
            {t('nav.contact')}
          </Link>
        </nav>

        <div className="mm__account">
          {isAuthenticated ? (
            <>
              <Link
                to="/profile"
                className="mm__account-item"
                onClick={() => setMenuOpen(false)}
              >
                {t('account.profile')}
              </Link>
              <Link
                to="/cart"
                className="mm__account-item"
                onClick={() => setMenuOpen(false)}
              >
                {t('account.orders')}
              </Link>
              <Link
                to="/notifications"
                className="mm__account-item"
                onClick={() => setMenuOpen(false)}
              >
                {t('account.notifications')} {unreadCount > 0 && `(${unreadCount})`}
              </Link>
              <button
                type="button"
                className="mm__account-item mm__account-item--danger"
                onClick={() => { handleLogout(); setMenuOpen(false); }}
              >
                {t('account.logout')}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="mm__login-btn"
              onClick={() => setMenuOpen(false)}
            >
              {t('account.login')}
            </Link>
          )}
        </div>

        <div className="mm__footer">
          <a href="https://wa.me/212669428878" className="mm__wa" target="_blank" rel="noreferrer">WhatsApp</a>
          <a href="https://instagram.com/madadline" className="mm__ig" target="_blank" rel="noreferrer">Instagram</a>
        </div>
      </aside>
    </>
  );
}

export default Header;